import { Injectable, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ReportsService, ProcessedControl } from './reports.service';
import { ControlConfig, ControlType } from '../../models/api/reports/report';
import { ControlValueHandler } from '../../utilities/control-value-handler';
import { CONTROL_SPECIAL_CASES } from './control-special-cases.config';

@Injectable({ providedIn: 'root' })
export class ControlConfigurationService {
  private reports = inject(ReportsService);


  private typeTransforms: Record<ControlType, (value: any) => any> = {
    date: (val) => {
      if (val instanceof Date) return val;
      if (typeof val === 'string') {
        // Handle MM-dd-yyyy format from database (e.g., "01-02-2026")
        const mmddyyyyMatch = val.match(/^(\d{2})-(\d{2})-(\d{4})$/);
        if (mmddyyyyMatch) {
          const [, month, day, year] = mmddyyyyMatch;
          return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
        }
      }
      return new Date(val);
    },
    currency: (val) => (typeof val === 'string' ? parseFloat(val) : val),
    number: (val) => (typeof val === 'string' ? parseFloat(val) : val),
    string: (val) => String(val),
    multiselect: (val) => val,
  };

  getTypeTransform(type?: ControlType): ((value: any) => any) | undefined {
    return type ? this.typeTransforms[type] : undefined;
  }

  getControl(erfName: string): ProcessedControl | undefined {
    const report = this.reports.currentReport();
    if (!report) return undefined;

    const controlsMap = report.reportRequest?.controlsMap;
    if (!controlsMap) return undefined;

    return controlsMap.get(erfName);
  }

  getControlOptions(erfName: string): { name: string; code: string }[] {
    const control = this.getControl(erfName);
    return (
      control?.options?.map((opt) => ({ name: opt.name, code: opt.code })) || []
    );
  }

  getControlLabel(erfName: string): string | undefined {
    return this.getControl(erfName)?.label;
  }

  getControlByNames(erfNames: string[]): ProcessedControl | undefined {
    const report = this.reports.currentReport();
    if (!report) return undefined;

    for (const name of erfNames) {
      const control = this.getControl(name);
      if (control) return control;
    }
    return undefined;
  }

  /** Sets visibility, stores control reference, and initializes form control value/validators. */
  loadControlConfiguration(
    config: ControlConfig,
    formGroup: FormGroup,
    storeReference?: (config: ControlConfig, control: ProcessedControl) => void,
    regionName?: string | null,
  ): void {
    const erfNamesArray = Array.isArray(config.erfNames)
      ? config.erfNames
      : [config.erfNames];

    const control = this.getControlByNames(erfNamesArray);

    if (!control) {
      config.visibility.set(false);
      return;
    }

    const erfName = erfNamesArray[0];

    if (!this.isValidRegion(control, erfName, regionName)) {
      config.visibility.set(false);
      return;
    }

    config.visibility.set(true);
    config.controlRef = control;

    if (storeReference) {
      storeReference(config, control);
    }

    const isNewControl = this.setFormControlValue(config, control, formGroup);
    const isRequired = config.required ?? control.required;
    this.setFormControlValidators(
      config.formControlName,
      isRequired,
      formGroup,
      config.validators,
      isNewControl, // Only clear existing validators for newly created controls
    );
  }

  /** Checks special cases from CONTROL_SPECIAL_CASES, then falls back to strict region matching. */
  private isValidRegion(
    control: ProcessedControl,
    erfName: string,
    regionName?: string | null,
  ): boolean {
    const specialCase = CONTROL_SPECIAL_CASES.find(
      (sc) => sc.erfName === erfName,
    );

    if (specialCase) {
      const mapping = specialCase.regionMappings.find(
        (rm) => rm.componentRegion === regionName,
      );
      if (mapping) {
        return mapping.allowedControlRegions.some(
          (region) => control.region === region,
        );
      }
    }

    if (regionName) {
      return control.region === regionName;
    }

    return true;
  }

  private getDefaultValue(
    control: ProcessedControl,
    config: ControlConfig,
  ): any {
    if (control.defaultValue && control.defaultValue.trim().length > 0) {
      return control.defaultValue;
    }

    if (config.defaultOptionCode && control.options?.length > 0) {
      const match = control.options.find(
        (o) => o.code === config.defaultOptionCode,
      );
      if (match) return match.code;
    }

    if (config.defaultToFirstOption && control.options?.length > 0) {
      return control.options[0].code;
    }

    return null;
  }

  private parseValue(
    value: any,
    control: ProcessedControl,
    config: ControlConfig,
  ): any {
    const isMultiSelect = config.isMultiSelect || config.type === 'multiselect';
    if (!value) {
      return isMultiSelect ? [] : null;
    }

    if (isMultiSelect) {
      const isCheckboxList =
        control.displayType === 'CHECKBOXLIST' ||
        control.displayType === 'MULTISELECTLISTBOX';
      return ControlValueHandler.parseMultiSelectValue(value, isCheckboxList);
    }

    return value;
  }

  private transformValue(value: any, config: ControlConfig): any {
    if (!value) return null;

    if (config.transformValue) {
      return config.transformValue(value);
    }

    if (config.type) {
      const typeTransform = this.getTypeTransform(config.type);
      if (typeTransform) {
        return typeTransform(value);
      }
    }

    return value;
  }

  setFormControlValue(
    config: ControlConfig,
    control: ProcessedControl,
    formGroup: FormGroup,
  ): boolean {
    let formControl = formGroup.get(config.formControlName);
    let isNew = false;

    if (!formControl) {
      formControl = new FormControl(null);
      formGroup.addControl(config.formControlName, formControl);
      isNew = true;
    }

    let value = this.getDefaultValue(control, config);

    if (value && typeof value === 'string' && control.options?.length > 0) {
      value = this.normalizeValueCase(value, control.options);
    }

    value = this.parseValue(value, control, config);
    value = this.transformValue(value, config);

    formControl.setValue(value, { emitEvent: false });
    return isNew;
  }

  setFormControlValidators(
    formControlName: string,
    required: boolean,
    formGroup: FormGroup,
    customValidators?: ValidatorFn[],
    clearExisting = true,
  ): void {
    const formControl = formGroup.get(formControlName);
    if (!formControl) return;

    if (clearExisting) {
      formControl.clearValidators();
    } else {
      if (!required) {
        formControl.removeValidators(Validators.required);
      }
    }
    if (required) {
      formControl.addValidators(Validators.required);
    }
    if (customValidators?.length) {
      formControl.addValidators(customValidators);
    }
    formControl.updateValueAndValidity({ emitEvent: false });
  }

  updateControlValue(control: ProcessedControl | undefined, value: any): void {
    if (!control) return;

    const report = this.reports.currentReport();
    const controlsMap = report.reportRequest?.controlsMap;
    if (!controlsMap) return;

    const formattedValue = ControlValueHandler.formatForApi(value);

    const updatedControl = {
      ...control,
      defaultValue: formattedValue,
    };

    if (formattedValue === undefined) {
      delete updatedControl.defaultValue;
    }

    controlsMap.set(control.erfName, updatedControl);
  }

  private normalizeValueCase(
    value: string,
    options: { code: string; name: string }[],
  ): string {
    if (value.includes('|')) {
      return value
        .split('|')
        .map((segment) => {
          const match = options.find(
            (o) => o.code.toLowerCase() === segment.toLowerCase(),
          );
          return match ? match.code : segment;
        })
        .join('|');
    }

    const match = options.find(
      (o) => o.code.toLowerCase() === value.toLowerCase(),
    );
    return match ? match.code : value;
  }
}
